/**
 * Industry-Standard Error Handling System
 * Provides comprehensive error handling, logging, and user feedback
 */

export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface AppError {
  id: string;
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  details?: string;
  statusCode?: number;
  timestamp: Date;
  userAgent?: string;
  url?: string;
  userId?: string;
  context?: Record<string, any>;
  stack?: string;
  retryable: boolean;
}

export interface ErrorHandlerOptions {
  showNotification?: boolean;
  logToConsole?: boolean;
  logToService?: boolean;
  retryable?: boolean;
  customMessage?: string;
}

class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: AppError[] = [];
  private maxLogSize = 100;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Create standardized error object
   */
  createError(
    type: ErrorType,
    message: string,
    options: {
      severity?: ErrorSeverity;
      details?: string;
      statusCode?: number;
      context?: Record<string, any>;
      retryable?: boolean;
    } = {}
  ): AppError {
    const error: AppError = {
      id: this.generateErrorId(),
      type,
      severity: options.severity || ErrorSeverity.MEDIUM,
      message,
      details: options.details,
      statusCode: options.statusCode,
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      context: options.context,
      retryable: options.retryable || false
    };

    return error;
  }

  /**
   * Handle API errors with standardized processing
   */
  async handleApiError(
    error: any,
    context: string,
    options: ErrorHandlerOptions = {}
  ): Promise<AppError> {
    let appError: AppError;

    if (error.response) {
      // HTTP error response
      const { status, data } = error.response;
      const type = this.getErrorTypeFromStatus(status);
      const severity = this.getSeverityFromStatus(status);

      appError = this.createError(type, data?.message || error.message, {
        severity,
        statusCode: status,
        details: data?.details || JSON.stringify(data),
        context: { apiContext: context, requestData: error.config?.data },
        retryable: this.isRetryableStatus(status)
      });
    } else if (error.request) {
      // Network error
      appError = this.createError(ErrorType.NETWORK, 'Network connection failed', {
        severity: ErrorSeverity.HIGH,
        details: 'Unable to reach the server. Please check your internet connection.',
        context: { apiContext: context },
        retryable: true
      });
    } else {
      // Client error
      appError = this.createError(ErrorType.CLIENT, error.message, {
        severity: ErrorSeverity.MEDIUM,
        details: error.stack,
        context: { apiContext: context },
        retryable: false
      });
    }

    return this.processError(appError, options);
  }

  /**
   * Handle validation errors
   */
  handleValidationError(
    field: string,
    message: string,
    value?: any,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const appError = this.createError(ErrorType.VALIDATION, message, {
      severity: ErrorSeverity.LOW,
      context: { field, value },
      retryable: false
    });

    return this.processError(appError, options);
  }

  /**
   * Handle authentication errors
   */
  handleAuthError(
    message: string,
    context: Record<string, any> = {},
    options: ErrorHandlerOptions = {}
  ): AppError {
    const appError = this.createError(ErrorType.AUTHENTICATION, message, {
      severity: ErrorSeverity.HIGH,
      context,
      retryable: false
    });

    return this.processError(appError, options);
  }

  /**
   * Handle authorization errors
   */
  handleAuthorizationError(
    resource: string,
    action: string,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const appError = this.createError(
      ErrorType.AUTHORIZATION,
      `Access denied: insufficient permissions for ${action} on ${resource}`,
      {
        severity: ErrorSeverity.MEDIUM,
        context: { resource, action },
        retryable: false
      }
    );

    return this.processError(appError, options);
  }

  /**
   * Handle unexpected errors
   */
  handleUnexpectedError(
    error: Error,
    context: string,
    options: ErrorHandlerOptions = {}
  ): AppError {
    const appError = this.createError(ErrorType.UNKNOWN, error.message, {
      severity: ErrorSeverity.HIGH,
      details: error.stack,
      context: { unexpectedContext: context },
      retryable: false
    });

    return this.processError(appError, options);
  }

  /**
   * Process error with logging and notifications
   */
  private processError(error: AppError, options: ErrorHandlerOptions): AppError {
    // Add to error log
    this.addToLog(error);

    // Console logging
    if (options.logToConsole !== false) {
      this.logToConsole(error);
    }

    // External logging service
    if (options.logToService) {
      this.logToService(error);
    }

    // Show user notification
    if (options.showNotification !== false) {
      this.showUserNotification(error, options.customMessage);
    }

    return error;
  }

  /**
   * Add error to internal log
   */
  private addToLog(error: AppError): void {
    this.errorLog.unshift(error);
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }
  }

  /**
   * Log error to console with proper formatting
   */
  private logToConsole(error: AppError): void {
    const style = this.getConsoleStyle(error.severity);
    console.groupCollapsed(`%c[${error.type}] ${error.message}`, style);
    console.log('Error ID:', error.id);
    console.log('Severity:', error.severity);
    console.log('Timestamp:', error.timestamp.toISOString());
    console.log('Context:', error.context);
    if (error.details) console.log('Details:', error.details);
    if (error.stack) console.log('Stack:', error.stack);
    console.groupEnd();
  }

  /**
   * Send error to external logging service
   */
  private async logToService(error: AppError): Promise<void> {
    try {
      // In a real implementation, this would send to your logging service
      // e.g., Sentry, LogRocket, DataDog, etc.
      
      // For now, we'll simulate the API call
      console.log('Would send to logging service:', {
        errorId: error.id,
        type: error.type,
        message: error.message,
        severity: error.severity,
        context: error.context
      });
    } catch (loggingError) {
      console.error('Failed to log error to service:', loggingError);
    }
  }

  /**
   * Show notification to user
   */
  private showUserNotification(error: AppError, customMessage?: string): void {
    const message = customMessage || this.getUserFriendlyMessage(error);
    
    // This would integrate with your notification system
    // For now, we'll use browser notification API as fallback
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Application Error', {
        body: message,
        icon: '/favicon.ico'
      });
    }
  }

  /**
   * Get user-friendly error message
   */
  private getUserFriendlyMessage(error: AppError): string {
    switch (error.type) {
      case ErrorType.NETWORK:
        return 'Connection problem. Please check your internet and try again.';
      case ErrorType.AUTHENTICATION:
        return 'Please log in again to continue.';
      case ErrorType.AUTHORIZATION:
        return 'You don\'t have permission to perform this action.';
      case ErrorType.VALIDATION:
        return error.message; // Validation messages are usually user-friendly
      case ErrorType.SERVER:
        return 'Server is temporarily unavailable. Please try again later.';
      default:
        return 'Something went wrong. Please try again or contact support.';
    }
  }

  /**
   * Utility methods
   */
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getErrorTypeFromStatus(status: number): ErrorType {
    if (status === 401) return ErrorType.AUTHENTICATION;
    if (status === 403) return ErrorType.AUTHORIZATION;
    if (status >= 400 && status < 500) return ErrorType.CLIENT;
    if (status >= 500) return ErrorType.SERVER;
    return ErrorType.UNKNOWN;
  }

  private getSeverityFromStatus(status: number): ErrorSeverity {
    if (status >= 500) return ErrorSeverity.HIGH;
    if (status === 401 || status === 403) return ErrorSeverity.MEDIUM;
    if (status >= 400) return ErrorSeverity.LOW;
    return ErrorSeverity.MEDIUM;
  }

  private isRetryableStatus(status: number): boolean {
    return [408, 429, 500, 502, 503, 504].includes(status);
  }

  private getConsoleStyle(severity: ErrorSeverity): string {
    switch (severity) {
      case ErrorSeverity.CRITICAL:
        return 'color: white; background-color: #dc2626; font-weight: bold; padding: 2px 4px;';
      case ErrorSeverity.HIGH:
        return 'color: #dc2626; font-weight: bold;';
      case ErrorSeverity.MEDIUM:
        return 'color: #f59e0b; font-weight: bold;';
      case ErrorSeverity.LOW:
        return 'color: #6b7280;';
      default:
        return 'color: #374151;';
    }
  }

  /**
   * Public methods for accessing errors
   */
  getRecentErrors(limit: number = 10): AppError[] {
    return this.errorLog.slice(0, limit);
  }

  getErrorById(id: string): AppError | undefined {
    return this.errorLog.find(error => error.id === id);
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }

  getErrorStats(): { 
    total: number; 
    byType: Record<ErrorType, number>; 
    bySeverity: Record<ErrorSeverity, number>;
  } {
    const stats = {
      total: this.errorLog.length,
      byType: {} as Record<ErrorType, number>,
      bySeverity: {} as Record<ErrorSeverity, number>
    };

    // Initialize counters
    Object.values(ErrorType).forEach(type => stats.byType[type] = 0);
    Object.values(ErrorSeverity).forEach(severity => stats.bySeverity[severity] = 0);

    // Count errors
    this.errorLog.forEach(error => {
      stats.byType[error.type]++;
      stats.bySeverity[error.severity]++;
    });

    return stats;
  }
}

export const errorHandler = ErrorHandler.getInstance();
export default errorHandler;